import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EmployeeProfileFormProps {
  employee: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const EmployeeProfileForm: React.FC<EmployeeProfileFormProps> = ({
  employee,
  open,
  onOpenChange,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    hourly_rate: '',
    overtime_rate: '',
    salary_type: 'hourly',
    payment_method: 'direct_deposit',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    medical_notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employee?.employee_profiles?.[0]) {
      const profile = employee.employee_profiles[0];
      setFormData({
        hourly_rate: profile.hourly_rate?.toString() || '',
        overtime_rate: profile.overtime_rate?.toString() || '',
        salary_type: profile.salary_type || 'hourly',
        payment_method: profile.payment_method || 'direct_deposit',
        emergency_contact_name: profile.emergency_contact_name || '',
        emergency_contact_phone: profile.emergency_contact_phone || '',
        emergency_contact_relation: profile.emergency_contact_relation || '',
        medical_notes: profile.medical_notes || ''
      });
    } else {
      // Calculate default overtime rate (1.5x hourly)
      const hourlyRate = parseFloat(formData.hourly_rate) || 15;
      setFormData(prev => ({
        ...prev,
        hourly_rate: prev.hourly_rate || '15.00',
        overtime_rate: prev.overtime_rate || (hourlyRate * 1.5).toFixed(2)
      }));
    }
  }, [employee]);

  const handleHourlyRateChange = (value: string) => {
    setFormData(prev => {
      const hourlyRate = parseFloat(value) || 0;
      return {
        ...prev,
        hourly_rate: value,
        overtime_rate: (hourlyRate * 1.5).toFixed(2)
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const profileData = {
        employee_id: employee.id,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        overtime_rate: parseFloat(formData.overtime_rate) || 0,
        salary_type: formData.salary_type,
        payment_method: formData.payment_method,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        emergency_contact_relation: formData.emergency_contact_relation || null,
        medical_notes: formData.medical_notes || null
      };

      if (employee.employee_profiles?.[0]) {
        // Update existing profile
        const { error } = await supabase
          .from('employee_profiles')
          .update(profileData)
          .eq('employee_id', employee.id);

        if (error) throw error;
      } else {
        // Create new profile
        const { error } = await supabase
          .from('employee_profiles')
          .insert([profileData]);

        if (error) throw error;
      }

      toast.success('Employee profile updated successfully!');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving employee profile:', error);
      toast.error('Failed to save employee profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Employee Profile - {employee?.first_name} {employee?.last_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
              <Input
                id="hourly_rate"
                type="number"
                step="0.01"
                min="0"
                value={formData.hourly_rate}
                onChange={(e) => handleHourlyRateChange(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="overtime_rate">Overtime Rate ($)</Label>
              <Input
                id="overtime_rate"
                type="number"
                step="0.01"
                min="0"
                value={formData.overtime_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, overtime_rate: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="salary_type">Salary Type</Label>
            <Select value={formData.salary_type} onValueChange={(value) => setFormData(prev => ({ ...prev, salary_type: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="salary">Salary</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_method">Payment Method</Label>
            <Select value={formData.payment_method} onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator className="my-6" />

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Emergency Contact</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_name">Full Name</Label>
                <Input
                  id="emergency_contact_name"
                  type="text"
                  maxLength={100}
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                  placeholder="Emergency contact name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="emergency_contact_phone">Phone Number</Label>
                <Input
                  id="emergency_contact_phone"
                  type="tel"
                  maxLength={20}
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergency_contact_relation">Relationship</Label>
              <Select value={formData.emergency_contact_relation} onValueChange={(value) => setFormData(prev => ({ ...prev, emergency_contact_relation: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="sibling">Sibling</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="friend">Friend</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="medical_notes">Medical Notes (Admin/Manager Only)</Label>
              <Textarea
                id="medical_notes"
                maxLength={500}
                rows={3}
                value={formData.medical_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, medical_notes: e.target.value }))}
                placeholder="Medical conditions, allergies, medications, etc."
              />
              <p className="text-xs text-muted-foreground">
                This information is confidential and only visible to administrators and managers.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};